import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Basic validation
      if (!formData.name || !formData.email || !formData.message) {
        throw new Error('Please fill in all required fields');
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Get IP address and location data
      const getLocationData = async () => {
        try {
          const response = await fetch('https://ipapi.co/json/');
          const data = await response.json();
          return {
            ip: data.ip,
            country: data.country_name,
            region: data.region,
            city: data.city,
            timezone: data.timezone,
            isp: data.org
          };
        } catch (error) {
          console.error('Error fetching location data:', error);
          return {
            ip: 'Unknown',
            country: 'Unknown',
            region: 'Unknown',
            city: 'Unknown',
            timezone: 'Unknown',
            isp: 'Unknown'
          };
        }
      };

      // Get device information
      const getDeviceInfo = () => {
        const userAgent = navigator.userAgent;
        let deviceType = 'Desktop';
        let os = 'Unknown';
        let browser = 'Unknown';

        // Detect device type
        if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
          if (/iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)|iPhone|iPod/i.test(userAgent)) {
            deviceType = /iPad/i.test(userAgent) ? 'Tablet' : 'Mobile';
          } else {
            deviceType = 'Mobile';
          }
        }

        // Detect OS
        if (/Windows/i.test(userAgent)) os = 'Windows';
        else if (/Mac/i.test(userAgent)) os = 'macOS';
        else if (/Linux/i.test(userAgent)) os = 'Linux';
        else if (/Android/i.test(userAgent)) os = 'Android';
        else if (/iPhone|iPad|iPod/i.test(userAgent)) os = 'iOS';

        // Detect browser
        if (/Chrome/i.test(userAgent) && !/Edge|Edg/i.test(userAgent)) browser = 'Chrome';
        else if (/Firefox/i.test(userAgent)) browser = 'Firefox';
        else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) browser = 'Safari';
        else if (/Edge|Edg/i.test(userAgent)) browser = 'Edge';
        else if (/Opera|OPR/i.test(userAgent)) browser = 'Opera';

        // Check for virtualized environments
        const isVirtualized = !!(
          /HeadlessChrome|PhantomJS|SlimerJS/i.test(userAgent) || 
          window.navigator.webdriver ||
          window.callPhantom ||
          window._phantom
        );

        return {
          deviceType,
          os,
          browser,
          userAgent,
          isVirtualized,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          language: navigator.language,
          platform: navigator.platform
        };
      };

      const [locationData, deviceInfo] = await Promise.all([
        getLocationData(),
        Promise.resolve(getDeviceInfo())
      ]);

      // Submit to Firestore
      await addDoc(collection(db, 'contacts'), {
        ...formData,
        timestamp: new Date().toISOString(),
        status: 'new',
        locationData,
        deviceInfo
      });

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      // Note: Contact submissions are saved to Firestore 'contacts' collection
      // Admin can view these in the admin panel
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#4d4d4d]">
      <h2 className="text-2xl font-bold mb-6 text-white">Get In Touch</h2>
      
      {submitStatus === 'success' && (
        <div className="mb-4 p-4 bg-green-900 border border-green-700 rounded text-green-100">
          Thank you for your message! I'll get back to you soon.
        </div>
      )}
      
      {submitStatus === 'error' && (
        <div className="mb-4 p-4 bg-red-900 border border-red-700 rounded text-red-100">
          There was an error sending your message. Please try again.
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
            placeholder="Your name"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
            placeholder="your.email@example.com"
          />
        </div>

        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-1">
            Subject
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
            placeholder="What's this about?"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows="5"
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500 resize-vertical"
            placeholder="Tell me about your project, question, or just say hello!"
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded transition duration-200"
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
};

export default ContactForm; 